import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import LookupSelect from '../components/LookupSelect';
import Modal from '../components/Modal';
import RelatedLinks from '../components/RelatedLinks';
import { useModal } from '../components/useModal';
import { useSortable } from '../../common/useSortable';
import { naturalCompare } from '../../common/utils';
import { lookupBookableProduct, lookupFacility } from '../lookups';

/**
 * Resource group — a set of physical things (courts, rooms) split into the
 * arrangements they can be sold as.
 *
 * Three levels, and the old page shows them nested because that is what they
 * are: the **group** belongs to a facility and optionally a locker; each
 * **split** is one way of dividing it; each **resource** in a split maps to a
 * product.
 *
 * Everything about this page destroys availability if it is wrong, so all four
 * destructive actions ask first — the old page removes a split, a resource or the
 * whole group on a bare button with no confirmation at all.
 *
 * Adding is the api's own two-step: `addSplit`/`addResource` create an empty row,
 * which you then name and save. Kept, because the endpoints work that way.
 *
 * Resources inside a split can be dragged into order; the order is saved and is
 * display only. Resources nobody has ordered are sorted by name, numbers read as
 * numbers ("Bane 2" before "Bane 10").
 */

const byDisplayOrderThenName = (a, b) =>
  (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity) || naturalCompare(a.name, b.name);

/**
 * One split: its resources, in the saved order, draggable within the split.
 * A component of its own because each split needs its own drag state.
 */
function SplitCard({
  api, split, index, busy, editing, setEditing,
  onAddResource, onRemoveSplit, onRemoveResource, onSaveResource, onReorder
}) {
  const resources = [...(split.resources || [])].sort(byDisplayOrderThenName);
  const sortable = useSortable({
    items: resources,
    getId: resource => resource.id,
    onReorder: orderedIds => onReorder(split, orderedIds),
    enabled: !busy && !editing && resources.length > 1
  });

  return (
    <section className="next-card">
      <div className="next-card__head">
        <h2>Split {index + 1}</h2>
        <span className="next-muted"><Guid value={split.id} /></span>
        <span className="next-card__spacer" />
        <button
          type="button"
          className="next-btn next-btn--sm"
          disabled={busy}
          onClick={() => onAddResource(split)}
        >
          Add resource
        </button>
        <button
          type="button"
          className="next-btn next-btn--sm next-btn--danger"
          disabled={busy}
          onClick={() => onRemoveSplit(split)}
        >
          Delete split
        </button>
      </div>

      <div className="next-table-wrap">
        <table className="next-table next-table--wide next-table--stack">
          <thead>
            <tr>
              <th aria-label="Order" />
              <th>Id</th>
              <th>Name</th>
              <th>Product</th>
              <th className="next-table__right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {resources.length === 0 && (
              <tr>
                <td colSpan={5} className="next-muted next-table__note">
                  No resource in this split yet.
                </td>
              </tr>
            )}

            {resources.map((resource, position) => {
              const isEditing = editing && editing.id === resource.id;
              const side = sortable.dropSide(resource);

              return (
                <tr
                  key={resource.id}
                  className={[
                    sortable.isDragging(resource) ? 'next-sortable__row--dragging' : '',
                    side ? `next-sortable__row--drop-${side}` : ''
                  ].join(' ')}
                  {...sortable.rowProps(resource)}
                >
                  <td data-label="" className="next-nowrap">
                    <span
                      className={`next-sortable__handle${sortable.enabled ? '' : ' next-sortable__handle--off'}`}
                      title="Drag to reorder"
                      {...sortable.handleProps(resource)}
                    >
                      ⠿
                    </span>
                    <span className="next-sortable__pos">{position + 1}</span>
                  </td>
                  <td data-label=""><Guid value={resource.id} /></td>
                  <td data-label="Name">
                    {isEditing ? (
                      <input
                        className="next-input"
                        value={editing.name}
                        onChange={event => setEditing({ ...editing, name: event.target.value })}
                      />
                    ) : (resource.name || <span className="next-muted">—</span>)}
                  </td>
                  <td data-label="Product">
                    {isEditing ? (
                      <LookupSelect
                        label="Product"
                        value={editing.productId}
                        onChange={value => setEditing({ ...editing, productId: value })}
                        loadOptions={key => lookupBookableProduct(api, key)}
                        placeholder="Name or id"
                      />
                    ) : (resource.product?.productName
                      || <span className="next-pill next-pill--warn">No product</span>)}
                  </td>
                  <td className="next-table__right" data-label="">
                    <div className="next-actions next-actions--end">
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            className="next-btn next-btn--sm next-btn--primary"
                            disabled={busy}
                            onClick={onSaveResource}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="next-btn next-btn--sm"
                            onClick={() => setEditing(null)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="next-btn next-btn--sm next-btn--quiet"
                            disabled={!sortable.enabled || position === 0}
                            onClick={() => sortable.move(resource.id, -1)}
                            aria-label="Move up"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className="next-btn next-btn--sm next-btn--quiet"
                            disabled={!sortable.enabled || position === resources.length - 1}
                            onClick={() => sortable.move(resource.id, 1)}
                            aria-label="Move down"
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            className="next-btn next-btn--sm"
                            onClick={() => setEditing({
                              id: resource.id,
                              name: resource.name || '',
                              productId: resource.productId || '',
                              productName: resource.product?.productName || '',
                              resourceSplitId: resource.resourceSplitId || split.id
                            })}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="next-btn next-btn--sm next-btn--danger"
                            disabled={busy}
                            onClick={() => onRemoveResource(resource)}
                          >
                            Remove
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

SplitCard.propTypes = {
  api: PropTypes.object.isRequired,
  split: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  busy: PropTypes.bool,
  editing: PropTypes.object,
  setEditing: PropTypes.func.isRequired,
  onAddResource: PropTypes.func.isRequired,
  onRemoveSplit: PropTypes.func.isRequired,
  onRemoveResource: PropTypes.func.isRequired,
  onSaveResource: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired
};

const ResourceGroupInternal = ({ api }) => {
  const { resourceGroupId } = useParams();
  const { ask, modalProps } = useModal();

  const [data, setData] = useState(null);
  const [lockers, setLockers] = useState([]);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  // The group's own fields, edited in place.
  const [form, setForm] = useState(null);
  // One resource at a time, keyed by id.
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    let group = null;
    try {
      group = await api.resources.getResourceGroup(resourceGroupId);
      if (!group || !group.group) {
        setFailed(true);
        return;
      }
      setData(group);
      setForm({
        id: group.group.id,
        name: group.group.name || '',
        facilityId: group.group.facilityId || '',
        lockerId: group.group.lockerId || ''
      });
      setFailed(false);
    } catch (error) {
      setFailed(true);
      return;
    }

    try {
      const answer = await api.lockers.getLockersByFacilityId(group.group.facilityId);
      const payload = answer?.success ? answer.data : null;
      const list = Array.isArray(payload)
        ? payload
        : (payload && (payload.id || payload.Id) ? [payload] : []);
      setLockers(list);
    } catch (error) {
      setLockers([]);
    }
  }, [api, resourceGroupId]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/resource-groups">← All resource groups</BackToList>
        <div className="next-note next-note--error">
          Could not load this resource group.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!data || !form) return <div className="next-page next-muted">Loading…</div>;

  const group = data.group;
  const splits = Array.isArray(data.relatedSplits) ? data.relatedSplits : [];

  const run = async (fn, okText, failText) => {
    setBusy(true);
    try {
      await fn();
      setNote({ tone: 'info', text: okText });
      await load();
    } catch (error) {
      setNote({ tone: 'error', text: `${failText}: ${error?.message || 'unknown error'}` });
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const dirty = form.name !== (group.name || '')
    || form.facilityId !== (group.facilityId || '')
    || form.lockerId !== (group.lockerId || '');

  const saveGroup = () => run(
    () => api.resources.saveResourceGroup({ ...group, ...form }),
    'Group saved.',
    'Unable to save the group'
  ).catch(() => {});

  const removeGroup = () => ask({
    kind: 'danger',
    title: `Delete ${group.name || 'this resource group'}?`,
    context: `Delete group · ${splits.length} split${splits.length === 1 ? '' : 's'}`,
    body: `The group goes, and with it its ${splits.length} split${splits.length === 1 ? '' : 's'} and every resource inside them. Products mapped to those resources stop having anything to sell.`,
    warning: 'This cannot be undone from the admin.',
    confirmLabel: 'Delete group',
    onConfirm: () => run(
      () => api.resources.removeGroup(group.id),
      'Group deleted.',
      'Unable to delete the group'
    )
  });

  const removeSplit = split => ask({
    kind: 'danger',
    title: 'Delete this split?',
    context: `Delete split · ${String(split.id).split('-')[0]}`,
    body: `Its ${(split.resources || []).length} resource${(split.resources || []).length === 1 ? '' : 's'} go with it, and the products mapped to them lose their slot in this group.`,
    confirmLabel: 'Delete split',
    onConfirm: () => run(
      () => api.resources.removeSplit(split.id),
      'Split deleted.',
      'Unable to delete the split'
    )
  });

  const removeResource = resource => ask({
    kind: 'danger',
    title: `Remove ${resource.name || 'this resource'}?`,
    context: 'Delete resource',
    body: `${resource.product?.productName || 'The product mapped to it'} loses this resource; anything already booked on it stays, but nothing new can be sold through it.`,
    confirmLabel: 'Remove resource',
    onConfirm: () => run(
      () => api.resources.removeResource(resource.id),
      'Resource removed.',
      'Unable to remove the resource'
    )
  });

  const addSplit = () => run(
    () => api.resources.addSplit(group.id),
    'Split added — name its resources below.',
    'Unable to add a split'
  ).catch(() => {});

  const addResource = split => run(
    () => api.resources.addResource(split.id),
    'Resource added — give it a name and a product.',
    'Unable to add a resource'
  ).catch(() => {});

  const reorderResources = async (split, orderedIds) => {
    // Shown at once; a failure reloads, which puts the saved order back.
    const byId = new Map((split.resources || []).map(resource => [resource.id, resource]));
    setData({
      ...data,
      relatedSplits: splits.map(other => (other.id !== split.id ? other : {
        ...other,
        resources: orderedIds.map((id, position) => ({ ...byId.get(id), displayOrder: position }))
      }))
    });
    try {
      await api.resources.reorderResources(split.id, orderedIds);
    } catch (error) {
      setNote({ tone: 'error', text: `Unable to save the order: ${error?.message || 'unknown error'}` });
      load();
    }
  };

  const saveResource = async () => {
    if (!editing) return;
    await run(
      () => api.resources.saveResource({
        Id: editing.id,
        name: editing.name,
        productId: editing.productId,
        productName: editing.productName,
        resourceSplitId: editing.resourceSplitId
      }),
      'Resource saved.',
      'Unable to save the resource'
    ).catch(() => {});
    setEditing(null);
  };

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/resource-groups">← All resource groups</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{group.name || 'Resource group'}</h1>
          <span className="next-pill next-pill--neutral">
            {splits.length} split{splits.length === 1 ? '' : 's'}
          </span>
        </div>
        <span className="next-detail__id">{group.id}</span>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head"><h2>Group</h2></div>
            <div className="next-form__grid">
              <label className="next-filter">
                <span className="next-filter__label">Name</span>
                <input
                  className="next-input"
                  value={form.name}
                  onChange={event => setForm({ ...form, name: event.target.value })}
                />
              </label>

              <LookupSelect
                label="Facility"
                value={form.facilityId}
                onChange={value => setForm({ ...form, facilityId: value })}
                loadOptions={key => lookupFacility(api, key)}
                placeholder="Name or id"
              />

              <label className="next-filter">
                <span className="next-filter__label">Locker</span>
                <select
                  className="next-input"
                  value={form.lockerId}
                  onChange={event => setForm({ ...form, lockerId: event.target.value })}
                >
                  <option value="">None</option>
                  {lockers.map(locker => (
                    <option key={locker.id || locker.Id} value={locker.id || locker.Id}>
                      {locker.name || locker.Name}
                    </option>
                  ))}
                </select>
                {lockers.length === 0 && (
                  <span className="next-form__hint">No locker at this facility.</span>
                )}
              </label>
            </div>

            <div className="next-form__actions">
              <button
                type="button"
                className="next-btn next-btn--primary"
                disabled={!dirty || busy}
                onClick={saveGroup}
              >
                Save group
              </button>
              <button
                type="button"
                className="next-btn"
                disabled={!dirty || busy}
                onClick={() => setForm({
                  id: group.id,
                  name: group.name || '',
                  facilityId: group.facilityId || '',
                  lockerId: group.lockerId || ''
                })}
              >
                Discard
              </button>
            </div>
          </section>

          {splits.map((split, index) => (
            <SplitCard
              key={split.id}
              api={api}
              split={split}
              index={index}
              busy={busy}
              editing={editing}
              setEditing={setEditing}
              onAddResource={addResource}
              onRemoveSplit={removeSplit}
              onRemoveResource={removeResource}
              onSaveResource={saveResource}
              onReorder={reorderResources}
            />
          ))}

          <div className="next-card">
            <div className="next-form__actions">
              <button
                type="button"
                className="next-btn"
                disabled={busy}
                onClick={addSplit}
              >
                Add a split
              </button>
              <span className="next-muted">
                A split is one way of dividing this group into sellable resources.
              </span>
            </div>
          </div>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Actions</h2></div>
            <div className="next-actionlist">
              <button
                type="button"
                className="next-actionlist__item next-actionlist__item--danger"
                onClick={removeGroup}
              >
                Delete group
              </button>
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                form.facilityId && {
                  label: 'Facility',
                  to: `/next/facility/${form.facilityId}`
                },
                form.facilityId && {
                  label: 'Other groups here',
                  hint: 'same facility',
                  to: `/next/resource-groups?facilityId=${form.facilityId}`
                },
                form.lockerId && { label: 'Locker', href: `/locker/${form.lockerId}` }
              ]}
            />
          </section>
        </aside>
      </div>

      <Modal {...modalProps} />
    </div>
  );
};

ResourceGroupInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const ResourceGroup = withApi(ResourceGroupInternal);

ResourceGroup.requiresLogin = true;

export default ResourceGroup;
